import { FormProvider, useForm } from 'react-hook-form';
import { vehicle as rules } from 'further-ui/validations';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { PropsWithChildren } from 'react';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { createEmptyFeeTemplate } from './helpers';
import PageLoader from 'components/PageLoader';
import { Typography } from '@mui/material';

const formSchema = rules.UpdateFeeSettings;

export type InferredFeeSettingsFormType = z.infer<typeof formSchema>;

type Props = {
  vehicleId: string;
};

const FormInner: React.FC<
  PropsWithChildren<{
    lpFeeTemplates: InferredFeeSettingsFormType['lpFeeTemplates'];
    payee: InferredFeeSettingsFormType['payee'];
  }>
> = ({ children, lpFeeTemplates, payee }) => {
  const formMethods = useForm<InferredFeeSettingsFormType>({
    shouldFocusError: true,
    resolver: zodResolver(formSchema),
    defaultValues: {
      payee,
      lpFeeTemplates: lpFeeTemplates?.length
        ? lpFeeTemplates
        : [createEmptyFeeTemplate(true)],
    },
    criteriaMode: 'all',
    mode: 'onChange',
  });

  return <FormProvider {...formMethods}>{children}</FormProvider>;
};

const FormWrapper: React.FC<PropsWithChildren<Props>> = ({
  children,
  vehicleId,
}) => {
  const { vehicle, isLoading } = useVehicle(vehicleId);

  if (isLoading) return <PageLoader />;
  if (!vehicle)
    return <Typography>Please select a fund to view fee settings.</Typography>;

  const lpFeeTemplates = vehicle.lpFeeTemplates.map((feeTemplate) => ({
    ...feeTemplate,
  })) as InferredFeeSettingsFormType['lpFeeTemplates'];

  return (
    <FormInner
      lpFeeTemplates={lpFeeTemplates}
      payee={{ name: vehicle.feePayee?.name ?? '' }}
    >
      {children}
    </FormInner>
  );
};

export default FormWrapper;
